import * as ModelEvent from "@node-elion/syncron";

import SubscriptionPool from "../../redux/services/SubscriptionPool";
import createLogger from "../../utils/logger.util";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import Subscription from "../../types/Subscription";
import { NonEditableProperties } from "../../types/NonEditableProperties";
import {
	TaxiServiceOld as TaxiService,
	Base,
	File,
	Client,
	Dispatcher,
	Executor,
	Language,
	PushNotice,
} from "..";

const logger = createLogger({ name: "PushMessage" });

class PushMessage extends Base {
	public static fromResponse(data: any): PushMessage.Model {
		return {
			id: data.id,
			additionalFields: data.additionalFields,
			bigPicture: data.bigPicture
				? File.fromResponse(data.bigPicture)
				: null,
			customer: data.customer ? Client.fromResponse(data.customer) : null,
			dispatcher: data.dispatcher
				? Dispatcher.fromResponse(data.dispatcher)
				: null,
			executor: data.executor
				? Executor.fromResponse(data.executor) || null
				: null,
			isAdvertising: data.isAdvertising,
			largeIcon: data.largeIcon
				? File.fromResponse(data.largeIcon)
				: null,
			notificationTemplate: data.notificationTemplate
				? PushNotice.fromResponse(data.notificationTemplate)
				: null,
			sender: data.sender,
			simpleStatus: data.simpleStatus,
			status: data.status,
			taxiService: data.taxiService
				? TaxiService.fromResponse(data.taxiService)
				: null,
			text: data.text,
			title: data.title,
			type: data.type,
			createdAt: data.createdAt,
			updatedAt: data.updatedAt,
			expiredAt: data.expiredAt,
		};
	}

	public static async subscribe(
		options: PushMessage.SubscribeOptions,
		onUpdate: Subscription.OnUpdate<PushMessage.Model>,
	): Promise<Subscription<PushMessage.SubscribeOptions> | null> {
		const modelEventConstructor = new ModelEvent.ModelEventConstructor({
			onUpdate: (state) => {
				logger.info("[PushMessage] subscribe ", {
					options,
					models: state.models,
				});
				onUpdate({
					...state,

					models: state.models.map(this.fromResponse),
				});
			},
		});

		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.notification.message.subscribe({
					params: this.optionsToRequest(options),
					ping: () => true,
					onEvent: (events) => {
						modelEventConstructor.onEvent(events);
					},
					onError: (error) => {
						logger.error(error);
					},
				}),
			{ name: "PushMessage.subscribe" },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (options: PushMessage.SubscribeOptions) =>
				subscription.update(this.optionsToRequest(options)),
		} as Subscription<PushMessage.SubscribeOptions>;
	}

	public static async getAll(options: PushMessage.SubscribeOptions) {
		try {
			const data = await this.request((prpc) =>
				prpc.theirsModel.notification.message.getAll(options),
			);

			const res: PushMessage.Model[] =
				data?.items?.map(this.fromResponse) || [];

			logger.info("[PushMessage] getAll", { options, res });

			return res;
		} catch (err) {
			logger.error("[PushMessage] error getAll", { options, err });
			return null;
		}
	}

	private static optionsToRequest(options: PushMessage.SubscribeOptions) {
		return {
			limit: options.limit,
			offset: options.offset,
			isActive: options.isActive,
			isAdvertising: options.isAdvertising,
			statuses: options.statuses,
			simpleStatuses: options.simpleStatuses,
			types: options.types,
			dateRange: options.dateRange,
			taxiServiceIds: options.taxiServiceIds,
			lang: options.language,
			order: options.order,
		};
	}
}

declare namespace PushMessage {
	interface Model extends Omit<NonEditableProperties, "deletedAt"> {
		additionalFields: {
			largeIconUrl?: string;
			bigPictureUrl?: string;
			i18n?: {
				titleKey: string;
				textKey: string;
				params?: Record<string, string>;
			};
		};
		bigPicture: File.Model | null;
		customer: Client.Model | null;
		dispatcher: Dispatcher.Model | null;
		executor: Executor.Model | null;
		isAdvertising: boolean;
		largeIcon: File.Model | null;
		notificationTemplate: PushNotice.Model | null;
		sender: string;
		simpleStatus: NotificationMessageSimpleStatus;
		status: NotificationMessageStatus;
		taxiService: TaxiService.Model | null;
		text: string | null;
		title: string | null;
		type: NotificationMessageType;
		expiredAt: string | null;
	}

	interface SubscribeOptions
		extends Omit<ServiceSubscribeOptionsBase<PushMessage.Model>, "query"> {
		isActive?: boolean;
		isAdvertising?: boolean;
		statuses?: NotificationMessageStatus[];
		simpleStatuses?: NotificationMessageSimpleStatus[];
		types?: NotificationMessageType[];
		dateRange?: {
			from: Date;
			to: Date;
		};
		taxiServiceIds?: number[];
		language?: Language;
	}
	type NotificationMessageSimpleStatus =
		| "DELIVERED"
		| "UNDELIVERABLE"
		| "READ";

	type NotificationMessageStatus =
		| "PENDING"
		| "PENDING_CONNECTION_TIMEOUT"
		| "ENROUTE"
		| "ENROUTE_COMPLETE"
		| "EXPIRED"
		| "SCHEDULED"
		| "DELETED"
		| "DELIVERED"
		| "UNDELIVERABLE"
		| "REJECTED"
		| "REJECTED_CONNECTION_TIMEOUT"
		| "READ";

	type NotificationMessageType = "info" | "order";
}

export default PushMessage;
